import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection, doc, getDoc, onSnapshot, orderBy, query, Timestamp, updateDoc, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";
import {
  getHeadbandzWord, leaveActiveFishbowl, passWord, sendFishbowlMessage,
  sendRelayPairMessage, submitClue, submitGuess, submitLaugh, submitSentence,
  submitTeamAnswer, swipe,
} from "../lib/api";
import { friendly } from "../lib/errors";
import {
  ACRO_GAME_MS, acroPhaseAt, clueRevealsWord, FISHBOWL_MSG_LIMIT, HB_HALF_MS, headbandzGiver,
  isSingleWordClue, leaderboard, mutualCompat, partnerFor, phaseAt, RELAY_TOTAL_MS, revealedTo,
  ROUND_MS, ROUNDS, rotationForRoom, shuffledOptions, validateAcrostic,
} from "../lib/relayGame";
import type { DeckCard, PairMessage, RoomDoc, RoomMessage, TeamAnswerDoc } from "../lib/types";
import Countdown, { useNow } from "../components/Countdown";
import Sheet from "../components/Sheet";
import ReportSheet from "../components/ReportSheet";
import ProfileDetail from "../components/ProfileDetail";
import { useVisualViewport } from "../lib/useVisualViewport";

export default function RelayRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const { uid } = useAuth();
  const navigate = useNavigate();
  const now = useNow(500);

  const [room, setRoom] = useState<RoomDoc | null>(null);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ uid: string; name: string } | null>(null);
  const [memberCard, setMemberCard] = useState<DeckCard | null>(null);
  const [likedNote, setLikedNote] = useState<string | null>(null);
  // Funny Sentences: the final-verdict leaderboard shows for a beat at the
  // top of the chat phase; tapping "Jump into the chat" skips it.
  const [skipResults, setSkipResults] = useState(false);
  const vv = useVisualViewport();

  // Errors show as a toast above the composer — clear them on their own so
  // they never linger over the game.
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 4000);
    return () => clearTimeout(t);
  }, [error]);

  /** Tap a REVEALED participant → their profile with like/pass (iOS parity). */
  async function openMember(uid2: string, name: string) {
    try {
      const snap = await getDoc(doc(db, "users", uid2));
      const d = snap.data() ?? {};
      setMemberCard({
        uid: uid2,
        displayName: (d.displayName as string) ?? name,
        age: (d.age as number) ?? 0,
        headline: (d.headline as string) ?? "",
        bio: (d.bio as string) ?? "",
        prompts: (d.prompts as { prompt: string; answer: string }[]) ?? [],
        photoURLs: (d.photoURLs as string[]) ?? [],
        locationName: "",
        distanceMiles: 0,
        boosted: false,
        compatibility: null,
        responsiveness: null,
      });
    } catch (e) {
      setError(friendly(e));
    }
  }

  async function likeMember(target: DeckCard) {
    try {
      const res = await swipe({ targetUid: target.uid, liked: true });
      setLikedNote(res.matched
        ? `It's a match with ${target.displayName}! 🎉 Find them in Matches.`
        : `Liked! If ${target.displayName} likes you back, it's a match.`);
    } catch (e) {
      setError(friendly(e));
    }
  }

  useEffect(() => {
    if (!roomId || !uid) return;
    return onSnapshot(
      doc(db, "fishbowls", roomId),
      (snap) => {
        if (!snap.exists()) { setGone(true); return; }
        setRoom(snap.data() as RoomDoc);
      },
      () => setGone(true),
    );
  }, [roomId, uid]);

  // Gender-gated identity: the room doc carries NO names. Your private
  // views/{uid} doc holds the names of the people you're allowed to see
  // (yourself + mutually compatible members) — same-side members simply
  // aren't in it, so their names never reach this client.
  const [viewNames, setViewNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!roomId || !uid) return;
    return onSnapshot(doc(db, "fishbowls", roomId, "views", uid), (snap) => {
      setViewNames((snap.data()?.names as Record<string, string>) ?? {});
    });
  }, [roomId, uid]);

  // When EVERYONE is ready, pull the lobby start in to ~5s (whichever client
  // notices first writes it; several writing the same ~now+5s is harmless).
  useEffect(() => {
    if (!room || !roomId || room.state !== "live") return;
    const started = room.startedAt?.toMillis() ?? 0;
    const allReady = room.participants.every((p) => room.ready?.[p] === true);
    if (allReady && started - Date.now() > 6000) {
      const s = Date.now() + 5000;
      void updateDoc(doc(db, "fishbowls", roomId), {
        startedAt: Timestamp.fromMillis(s),
        endsAt: Timestamp.fromMillis(s + RELAY_TOTAL_MS),
      }).catch(() => {});
    }
  }, [room, roomId]);

  async function leave() {
    // Mark this room as exited BEFORE navigating so the Relay home's
    // auto-enter listener can never bounce us back in on a stale flag.
    if (roomId) sessionStorage.setItem("fuse-left-room", roomId);
    try {
      await leaveActiveFishbowl();
    } catch (e) {
      setError(friendly(e));
    }
    navigate("/relay", { replace: true });
  }

  // Room over (clock ran out or the doc was swept) → straight back to the
  // Relay home. No "Back to Relay" button, no dead-end screen.
  const leftRef = useRef(false);
  useEffect(() => {
    const startedMs = room?.startedAt?.toMillis();
    const endedNow =
      gone ||
      (room != null && startedMs != null &&
        (room.gameType === "acro" ? acroPhaseAt(startedMs, now) : phaseAt(startedMs, now)).phase === "ended");
    if (endedNow && !leftRef.current) {
      leftRef.current = true;
      void leave();
    }
  }, [gone, room, now]); // eslint-disable-line react-hooks/exhaustive-deps

  if (gone) {
    // Auto-leaving (effect above) — just show a beat of spinner on the way out.
    return <div className="splash"><div className="spinner" /></div>;
  }
  if (!room || !uid || !roomId) return <div className="splash"><div className="spinner" /></div>;

  // Merge the gender-gated names from the private view doc into the room the
  // children render from — anyone missing from it renders as masked.
  const roomV: RoomDoc = { ...room, names: viewNames };
  const startedAt = room.startedAt?.toMillis() ?? now;
  const phase = room.gameType === "acro" ? acroPhaseAt(startedAt, now) : phaseAt(startedAt, now);
  const myName = viewNames[uid] ?? "You";
  // Progressive reveal, gender-gated: same-side people stay masked all relay.
  const revealed = revealedTo(uid, room.participants, room.genders, room.seekings, room.gameType, startedAt, now);
  const iAmReady = room.ready?.[uid] === true;
  const readyCount = room.participants.filter((p) => room.ready?.[p] === true).length;
  const readyLocked = startedAt - now <= 5000; // last 5s → readying does nothing

  return (
    <div
      className={"chat-screen" + (vv.height ? " kb-open" : "")}
      style={vv.height ? { height: vv.height, transform: `translateY(${vv.top ?? 0}px)` } : undefined}
    >
      <header className="chat-header">
        <button className="chat-back" aria-label="Leave" onClick={() => setConfirmLeave(true)}>‹</button>
        <div className="chat-title">
          <div className="chat-name">
            {room.gameType === "trivia" ? "Relay · Trivia" : room.gameType === "headbandz" ? "Relay · Headbandz" : "Relay · Funny Sentences"}
          </div>
          <div className="chat-sub">{room.participants.length} people</div>
        </div>
        {/* ONE clock per screen: during gameplay each game's strip carries its
            own meaningful timer, so the header only counts down chat/decide. */}
        {(phase.phase === "chat" || phase.phase === "decide") && (
          <span className="pill"><Countdown endsAt={phase.endsAt} /></span>
        )}
        {phase.phase === "trivia" && room.gameType === "trivia" && (
          <span className="pill"><Countdown endsAt={phase.questionEndsAt} /></span>
        )}
      </header>

      {phase.phase === "lobby" && (
        <div className="screen center" style={{ paddingTop: 40 }}>
          <div className="pulse-ring" />
          <h2>Starting in <Countdown endsAt={startedAt} /></h2>
          <p className="muted">
            Your room is set. Profiles unlock as you play — and only across the
            aisle: people on your own side stay anonymous the whole relay.
          </p>
          <button
            className={"btn" + (iAmReady ? " btn-secondary" : "")}
            disabled={iAmReady || readyLocked}
            onClick={() => {
              if (iAmReady || readyLocked) return;
              void updateDoc(doc(db, "fishbowls", roomId), { [`ready.${uid}`]: true })
                .catch((e) => setError(friendly(e)));
            }}
          >
            {iAmReady ? "Ready ✓" : readyLocked ? "Starting…" : "I'm ready — skip the wait"}
          </button>
          <p className="muted" style={{ fontSize: 13 }}>
            {readyCount} of {room.participants.length} ready.
            {" "}Everyone ready and it starts in 5 seconds.
          </p>
          <Roster room={roomV} me={uid} revealed={revealed} onMember={openMember} />
        </div>
      )}

      {phase.phase !== "lobby" && phase.phase !== "ended" && phase.phase !== "chat" && phase.phase !== "decide" && room.gameType === "trivia" && (
        <TriviaPhase room={roomV} roomId={roomId} me={uid} startedAt={startedAt} now={now} setError={setError} />
      )}
      {phase.phase !== "lobby" && phase.phase !== "ended" && phase.phase !== "chat" && phase.phase !== "decide" && room.gameType === "headbandz" && (
        <HeadbandzPhase room={roomV} roomId={roomId} me={uid} startedAt={startedAt} now={now} setError={setError} />
      )}
      {room.gameType === "acro" && (phase.phase === "write" || phase.phase === "reveal" || phase.phase === "vote") && (
        <AcroPhase room={roomV} roomId={roomId} me={uid} phase={phase} setError={setError} />
      )}

      {phase.phase === "chat" && (
        room.gameType === "acro" && !skipResults && now - startedAt < ACRO_GAME_MS + ACRO_RESULTS_MS ? (
          <AcroResults
            room={roomV}
            roomId={roomId}
            me={uid}
            revealed={revealed}
            endsAt={startedAt + ACRO_GAME_MS + ACRO_RESULTS_MS}
            onSkip={() => setSkipResults(true)}
          />
        ) : (
          <RoomChat room={roomV} roomId={roomId} me={uid} revealed={revealed} setError={setError} onMember={openMember} />
        )
      )}

      {phase.phase === "decide" && (
        <DecidePhase room={roomV} me={uid} revealed={revealed} endsAt={phase.endsAt} onMember={openMember} setError={setError} />
      )}

      {phase.phase === "ended" && (
        // Auto-leaving (effect above) — spinner while we head home.
        <div className="splash"><div className="spinner" /></div>
      )}

      {error && <div className="error">{error}</div>}

      <Sheet open={confirmLeave} onClose={() => setConfirmLeave(false)} title="Leave this Relay?">
        <p className="muted">The room keeps going without you. This still counts toward today's Relays.</p>
        <button className="btn btn-danger" onClick={() => void leave()}>Leave</button>
        <button className="btn btn-ghost" onClick={() => setConfirmLeave(false)}>Stay</button>
      </Sheet>

      {memberCard && (
        <ProfileDetail
          profile={memberCard}
          actionsFlush
          onClose={() => setMemberCard(null)}
          onLike={() => void likeMember(memberCard)}
          onPass={() => {}}
          onReport={() => setReportTarget({ uid: memberCard.uid, name: memberCard.displayName })}
        />
      )}

      <Sheet open={!!likedNote} onClose={() => setLikedNote(null)}>
        <div className="center">
          <div style={{ fontSize: 40 }}>⚡</div>
          <p style={{ marginTop: 8 }}>{likedNote}</p>
          <button className="btn" onClick={() => setLikedNote(null)}>Keep playing</button>
        </div>
      </Sheet>

      {reportTarget && (
        <ReportSheet
          targetUid={reportTarget.uid}
          targetName={reportTarget.name}
          open={!!reportTarget}
          onClose={() => setReportTarget(null)}
          onDone={() => setReportTarget(null)}
        />
      )}
      <span style={{ display: "none" }}>{myName}</span>
    </div>
  );
}

/** Roster with progressive reveal: paired people show their name and open a
 * profile; everyone else is a masked "Hidden" chip (iOS parity). */
function Roster({
  room, me, revealed, onMember,
}: {
  room: RoomDoc; me: string; revealed: Set<string>;
  onMember: (uid: string, name: string) => void;
}) {
  return (
    <div className="roster">
      {room.participants.map((p) => {
        const isRevealed = revealed.has(p);
        return (
          <button
            key={p}
            className={"roster-chip" + (isRevealed && p !== me ? " roster-revealed" : "")}
            disabled={p === me || !isRevealed}
            onClick={() => void onMember(p, room.names[p] ?? "Someone")}
            title={p === me ? "You" : isRevealed ? "View profile" : "Play a round together to reveal"}
          >
            {isRevealed ? (room.names[p] ?? "Someone") : "? Hidden"}{p === me ? " (you)" : ""}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pair chat (shared by trivia + headbandz rounds)
// ---------------------------------------------------------------------------
function usePairMessages(roomId: string, pid: string | null) {
  const [msgs, setMsgs] = useState<PairMessage[]>([]);
  useEffect(() => {
    if (!pid) { setMsgs([]); return; }
    const q = query(collection(db, "fishbowls", roomId, "pairMessages"), where("pairId", "==", pid));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() as Omit<PairMessage, "id">) }))
        .sort((a, b) => (a.sentAt?.toMillis() ?? Infinity) - (b.sentAt?.toMillis() ?? Infinity));
      setMsgs(rows);
    });
  }, [roomId, pid]);
  return msgs;
}

function PairChat({
  msgs, me, onSend, placeholder, disabled, onTyping,
}: {
  msgs: PairMessage[]; me: string; onSend: (text: string) => Promise<void>;
  placeholder: string; disabled?: boolean;
  /** True while a draft is being written — lets the parent slide bulky UI
   * (the question card) out of the way so the composer stays visible. */
  onTyping?: (typing: boolean) => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll only this pane (scrollIntoView also scrolls the document on iOS).
    const el = endRef.current?.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs.length]);

  // When the keyboard opens the pane shrinks — keep it pinned to the latest
  // message instead of leaving the newest ones hidden below the fold.
  useEffect(() => {
    const el = endRef.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => { el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  async function send() {
    const t = draft.trim();
    if (!t || busy) return;
    setBusy(true);
    // Clear right away so the next clue/guess can be typed while this one is
    // in flight (errors surface via the room's error line). Sending also
    // brings the question back (onTyping false).
    setDraft("");
    onTyping?.(false);
    try {
      await onSend(t);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="pair-chat">
        {msgs.map((m) =>
          (m as { kind?: string }).kind === "reveal" ? (
            <div key={m.id} className="reveal-line">✓ The word was “{m.text}”</div>
          ) : (
            <div key={m.id} className={"bubble-row" + (m.senderId === me ? " mine" : "")}>
              <div className="bubble bubble-sm">
                {(m as { kind?: string }).kind === "guess" && <span className="muted">guess: </span>}
                {m.text}
              </div>
            </div>
          ),
        )}
        <div ref={endRef} />
      </div>
      <div className="chat-compose">
        <div className="compose-pill">
          {/* A real single-line input: placeholders can't wrap-and-clip like
              they do in a rows=1 textarea on iOS. */}
          <input
            type="text"
            value={draft}
            maxLength={2000}
            enterKeyHint="send"
            placeholder={placeholder}
            /* never `busy` — disabling a focused input dismisses the iOS
               keyboard mid-game, causing the jump-and-relayout dance */
            disabled={disabled}
            onChange={(e) => { setDraft(e.target.value); onTyping?.(e.target.value.trim().length > 0); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void send(); } }}
          />
          <button className="send-btn" aria-label="Send" disabled={disabled || busy || !draft.trim()} onMouseDown={(e) => e.preventDefault()} onClick={() => void send()}>➤</button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Trivia
// ---------------------------------------------------------------------------
function TriviaPhase({
  room, roomId, me, startedAt, now, setError,
}: {
  room: RoomDoc; roomId: string; me: string; startedAt: number; now: number;
  setError: (s: string | null) => void;
}) {
  const phase = phaseAt(startedAt, now);
  const [answers, setAnswers] = useState<Record<string, TeamAnswerDoc>>({});
  const [locking, setLocking] = useState<string | null>(null);
  // While a chat draft is being written, the question card slides out of the
  // way so the composer + draft stay visible over the keyboard; sending (or
  // clearing the draft) brings it back.
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    return onSnapshot(collection(db, "fishbowls", roomId, "answers"), (snap) => {
      const map: Record<string, TeamAnswerDoc> = {};
      snap.docs.forEach((d) => { map[d.id] = d.data() as TeamAnswerDoc; });
      setAnswers(map);
    });
  }, [roomId]);

  const pc = phase.phase === "trivia" ? partnerFor(room.participants, room.genders, me, phase.round) : null;
  const msgs = usePairMessages(roomId, pc?.pid ?? null);

  if (phase.phase !== "trivia") return null;
  const q = room.questions?.[phase.questionIndex];
  const locked = pc ? answers[`${phase.questionIndex}_${pc.pid}`] : undefined;

  async function lock(answer: string) {
    if (locking || locked) return;
    setLocking(answer);
    setError(null);
    try {
      await submitTeamAnswer({ fishbowlId: roomId, questionIndex: phase.phase === "trivia" ? phase.questionIndex : 0, answer });
    } catch (e) {
      setError(friendly(e));
    } finally {
      setLocking(null);
    }
  }

  return (
    <div className="game-wrap">
      <div className="round-strip">
        Round {phase.round + 1}/{ROUNDS} · Q{(phase.questionIndex % 5) + 1}/5 · with{" "}
        <b>{pc ? room.names[pc.partner] ?? "your partner" : "…"}</b>
      </div>
      {q ? (
        <div className={"question-card" + (typing ? " q-collapsed" : "")}>
          <div className="q-cat">{q.category}</div>
          <div className="q-text">{q.question}</div>
          <div className="q-opts">
            {shuffledOptions(q, phase.questionIndex).map((opt) => {
              const isLocked = locked?.answer === opt;
              // Once the team locks, the truth shows immediately: the correct
              // option goes green; a wrong locked pick goes red.
              const showCorrect = !!locked && opt === q.correct;
              const showWrong = !!locked && isLocked && locked.answer !== q.correct;
              return (
                <button
                  key={opt}
                  className={
                    "q-opt"
                    + (isLocked ? " q-opt-locked" : "")
                    + (locked && !isLocked && !showCorrect ? " q-opt-dim" : "")
                    + (showCorrect ? " q-opt-correct" : "")
                    + (showWrong ? " q-opt-wrong" : "")
                  }
                  disabled={!!locked || !!locking}
                  onClick={() => void lock(opt)}
                >
                  {opt}
                  {showCorrect && <span style={{ fontWeight: 700 }}> ✓</span>}
                  {showWrong && <span style={{ fontWeight: 700 }}> ✗</span>}
                  {isLocked && <span className="muted"> · locked{locked?.lockedBy === me ? " by you" : ` by ${room.names[locked.lockedBy] ?? "partner"}`}</span>}
                </button>
              );
            })}
          </div>
          {!locked && <p className="muted" style={{ fontSize: 12 }}>Talk it over below — first to tap locks the team answer.</p>}
        </div>
      ) : (
        <div className={"stub" + (typing ? " q-collapsed" : "")}>Loading question…</div>
      )}
      <PairChat
        msgs={msgs}
        me={me}
        onTyping={setTyping}
        placeholder={pc ? `Chat with ${room.names[pc.partner] ?? "partner"}…` : "…"}
        onSend={async (text) => {
          try {
            await sendRelayPairMessage({ fishbowlId: roomId, text });
          } catch (e) {
            setError(friendly(e));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Headbandz
// ---------------------------------------------------------------------------
function HeadbandzPhase({
  room, roomId, me, startedAt, now, setError,
}: {
  room: RoomDoc; roomId: string; me: string; startedAt: number; now: number;
  setError: (s: string | null) => void;
}) {
  const phase = phaseAt(startedAt, now);
  const round = phase.phase === "trivia" ? phase.round : 0;
  // The giver swap depends on the SCHEDULE order of the pair (pair[0] gives the
  // first half), not the sorted pairId order — so pull the raw pair.
  const schedule = rotationForRoom(room.participants, room.genders);
  const rawPair = (schedule[round] ?? []).find((p) => p.includes(me)) ?? null;
  const pc = partnerFor(room.participants, room.genders, me, round);
  const msgs = usePairMessages(roomId, pc?.pid ?? null);

  const [pairState, setPairState] = useState<{
    score?: number; wordCursor?: number; cursor0?: number; cursor1?: number;
  } | null>(null);
  const [word, setWord] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const msIntoRound = (now - startedAt) - round * ROUND_MS;
  const giver = rawPair ? headbandzGiver(rawPair, msIntoRound) : null;
  const isGiver = giver === me;
  // Word cursors are PER HALF (the server deals each half its own fresh
  // sequence, so the incoming giver never inherits the outgoing giver's word).
  const half = msIntoRound < HB_HALF_MS ? 0 : 1;
  const cursor = (half === 0
    ? pairState?.cursor0 ?? pairState?.wordCursor
    : pairState?.cursor1) ?? 0;
  const halfEndsAt = startedAt + round * ROUND_MS + (half === 0 ? HB_HALF_MS : ROUND_MS);

  useEffect(() => {
    if (!pc) return;
    return onSnapshot(doc(db, "fishbowls", roomId, "headbandz", `${round}_${pc.pid}`), (s) => {
      setPairState((s.data() as { score?: number; wordCursor?: number }) ?? null);
    });
  }, [roomId, round, pc?.pid]); // eslint-disable-line react-hooks/exhaustive-deps

  // The giver fetches the secret word; refetch when the cursor advances or
  // roles swap. The prefetched `next` makes the flip instant on correct/pass.
  const nextWordRef = useRef<string | null>(null);
  const lastCursorRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isGiver) { setWord(null); nextWordRef.current = null; lastCursorRef.current = null; return; }
    // Only flip the display when the cursor actually ADVANCED — on mount keep
    // whatever is showing (e.g. the optimistic skip); the fetch confirms it.
    const advanced = lastCursorRef.current !== null && lastCursorRef.current !== cursor;
    lastCursorRef.current = cursor;
    if (advanced) {
      setWord((w) => nextWordRef.current ?? w);
      nextWordRef.current = null;
    }
    let alive = true;
    void getHeadbandzWord({ fishbowlId: roomId })
      .then((r) => {
        if (!alive) return;
        setWord(r.word ?? null);
        nextWordRef.current = r.next ?? null;
      })
      .catch((e) => setError(friendly(e)));
    return () => { alive = false; };
  }, [isGiver, cursor, half, roomId, round, setError]);

  async function pass() {
    if (busy) return;
    setBusy(true);
    // Flip to the cached next word IMMEDIATELY — the server call catches up
    // in the background (reverted if it fails).
    const prev = word;
    const optimistic = nextWordRef.current;
    if (optimistic) { setWord(optimistic); nextWordRef.current = null; }
    try {
      await passWord({ fishbowlId: roomId });
    } catch (e) {
      if (optimistic) setWord(prev);
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  const score = pairState?.score ?? 0;
  const partnerName = pc ? room.names[pc.partner] ?? "Partner" : "…";
  return (
    <div className="game-wrap">
      <div className="round-strip">
        Round {round + 1}/{ROUNDS} · {isGiver ? "you give the clues" : "you guess"}
        {" "}· team score <b>{score}</b>
        {" "}· {half === 0 ? "roles swap" : "round ends"} in <Countdown endsAt={halfEndsAt} />
      </div>
      {/* Faces flank the word. ONE team score (in the strip above) — you and
          your partner are a team here, not opponents. */}
      <div className="duel-bar">
        <div className="duel-side">
          <div className="duel-face">
            <div className="avatar-ring">☺</div>
            <div className="p-name">You</div>
          </div>
        </div>
        {isGiver ? (
          <div className="duel-mid">
            <span className="hb-label">Their word · tap it to skip</span>
            {/* The word IS the skip button — same interaction as the 1v1. */}
            <button
              className="hb-word-btn"
              key={word ?? "loading"}
              disabled={busy || !word}
              /* keep the composer focused — skipping must not dismiss the keyboard */
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void pass()}
            >
              {word ?? <span className="muted" style={{ fontWeight: 600, fontSize: 12 }}>getting your word…</span>}
            </button>
          </div>
        ) : (
          <div className="duel-mid">
            <span className="hb-label">Your word is secret</span>
            <span style={{ fontWeight: 700 }}>Guess from the clues!</span>
          </div>
        )}
        <div className="duel-side">
          <div className="duel-face">
            <div className="avatar-ring">☺</div>
            <div className="p-name">{partnerName}</div>
          </div>
        </div>
      </div>
      <PairChat
        msgs={msgs}
        me={me}
        placeholder={isGiver ? "Your clue…" : "Your guess…"}
        onSend={async (text) => {
          try {
            if (isGiver) {
              if (!isSingleWordClue(text)) {
                setError("Clues must be a single word.");
                return;
              }
              if (word && clueRevealsWord(text, word)) {
                setError("Nice try — you can't say the word itself!");
                return;
              }
              await submitClue({ fishbowlId: roomId, text });
            } else {
              await submitGuess({ fishbowlId: roomId, text });
            }
          } catch (e) {
            setError(friendly(e));
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funny Sentences (acro)
// ---------------------------------------------------------------------------
function AcroPhase({
  room, roomId, me, phase, setError,
}: {
  room: RoomDoc; roomId: string; me: string;
  phase: { phase: "write" | "reveal" | "vote"; round: number; endsAt: number };
  setError: (s: string | null) => void;
}) {
  const letters = room.acroLetters?.[String(phase.round)] ?? [];
  const [entries, setEntries] = useState<Record<string, { authorId: string; sentence: string }>>({});
  const [laughs, setLaughs] = useState<Record<string, { voterId: string; targetId: string; round: number }>>({});
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const u1 = onSnapshot(collection(db, "fishbowls", roomId, "acroEntries"), (s) => {
      const map: Record<string, { authorId: string; sentence: string }> = {};
      s.docs.forEach((d) => { map[d.id] = d.data() as { authorId: string; sentence: string }; });
      setEntries(map);
    });
    const u2 = onSnapshot(collection(db, "fishbowls", roomId, "acroLaughs"), (s) => {
      const map: Record<string, { voterId: string; targetId: string; round: number }> = {};
      s.docs.forEach((d) => { map[d.id] = d.data() as { voterId: string; targetId: string; round: number }; });
      setLaughs(map);
    });
    return () => { u1(); u2(); };
  }, [roomId]);

  const mine = entries[`${phase.round}_${me}`];
  const roundEntries = Object.entries(entries)
    .filter(([id]) => id.startsWith(`${phase.round}_`))
    .map(([, v]) => v);

  // Laughs 😂 — the only reaction. Totals across rounds are the scoreboard.
  const laughedByMe = useMemo(() => {
    const s = new Set<string>();
    Object.values(laughs).forEach((l) => {
      if (l.voterId === me && l.round === phase.round) s.add(l.targetId);
    });
    return s;
  }, [laughs, me, phase.round]);
  const roundLaughCounts = useMemo(() => {
    const s: Record<string, number> = {};
    Object.values(laughs).forEach((l) => {
      if (l.round === phase.round) s[l.targetId] = (s[l.targetId] ?? 0) + 1;
    });
    return s;
  }, [laughs, phase.round]);
  const scores = useMemo(() => {
    const s: Record<string, number> = {};
    Object.values(laughs).forEach((l) => { s[l.targetId] = (s[l.targetId] ?? 0) + 1; });
    return s;
  }, [laughs]);

  const valid = validateAcrostic(draft, letters);

  async function submit() {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await submitSentence({ fishbowlId: roomId, round: phase.round, sentence: draft.trim() });
      setDraft("");
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  async function laugh(targetId: string) {
    if (busy || laughedByMe.has(targetId) || targetId === me) return;
    setBusy(true);
    setError(null);
    try {
      await submitLaugh({ fishbowlId: roomId, round: phase.round, targetId });
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="game-wrap" style={{ overflowY: "auto" }}>
      <div className="round-strip">
        Round {phase.round + 1}/5 · {phase.phase === "write" ? "write" : "read & laugh"}
        {" "}· <Countdown endsAt={phase.phase === "write" ? phase.endsAt : (phase.endsAt + (phase.phase === "reveal" ? 10_000 : 0))} />
      </div>
      <div className="letters-row">
        {letters.map((l, i) => <span key={i} className="letter-chip">{l.toUpperCase()}</span>)}
      </div>

      {phase.phase === "write" && (
        <div className="question-card">
          <div className="q-cat">Write a sentence</div>
          <p className="muted" style={{ fontSize: 13 }}>
            One word per letter, in order. Most laughs across the rounds wins.
          </p>
          {mine ? (
            <p><b>Yours:</b> {mine.sentence}</p>
          ) : (
            <>
              <textarea
                value={draft}
                maxLength={300}
                placeholder={letters.map((l) => l.toUpperCase() + "…").join(" ")}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button className="btn" disabled={!valid || busy} onClick={() => void submit()}>
                {valid ? "Submit" : "Match every letter to submit"}
              </button>
            </>
          )}
        </div>
      )}

      {(phase.phase === "reveal" || phase.phase === "vote") && (
        <div className="question-card">
          <div className="q-cat">The sentences — 😂 the ones that get you</div>
          {roundEntries.length === 0 && <p className="muted">Nobody submitted this round 😅</p>}
          {roundEntries.map((e) => {
            const laughed = laughedByMe.has(e.authorId);
            const count = roundLaughCounts[e.authorId] ?? 0;
            return (
              <div key={e.authorId} className="q-opt" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  “{e.sentence}”
                  <span className="muted"> — {e.authorId === me ? "you" : room.names[e.authorId] ?? "🙈"}</span>
                </span>
                {e.authorId !== me && (
                  <button
                    className={"btn-ghost-sm" + (laughed ? " laughed" : "")}
                    style={{ margin: 0, fontSize: 18, opacity: laughed ? 1 : 0.55 }}
                    aria-label="Laugh"
                    disabled={laughed || busy}
                    onClick={() => void laugh(e.authorId)}
                  >
                    😂{count > 0 ? <b style={{ fontSize: 13 }}> {count}</b> : null}
                  </button>
                )}
                {e.authorId === me && count > 0 && <span style={{ fontSize: 14 }}>😂 <b>{count}</b></span>}
              </div>
            );
          })}
        </div>
      )}

      <div className="mini-board">
        {room.participants
          .map((p) => ({ p, s: scores[p] ?? 0 }))
          .sort((a, b) => b.s - a.s)
          .slice(0, 4)
          .map(({ p, s }) => (
            <span key={p} className="pill">{p === me ? "You" : room.names[p] ?? "🙈"} 😂{s}</span>
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Funny Sentences finale: the verdict — funniest to least funny, then chat.
// ---------------------------------------------------------------------------
const ACRO_RESULTS_MS = 12_000; // how long the leaderboard holds before chat

function AcroResults({
  room, roomId, me, revealed, endsAt, onSkip,
}: {
  room: RoomDoc; roomId: string; me: string; revealed: Set<string>;
  endsAt: number; onSkip: () => void;
}) {
  const [laughs, setLaughs] = useState<Record<string, { targetId: string }>>({});
  useEffect(() => {
    return onSnapshot(collection(db, "fishbowls", roomId, "acroLaughs"), (s) => {
      const map: Record<string, { targetId: string }> = {};
      s.docs.forEach((d) => { map[d.id] = d.data() as { targetId: string }; });
      setLaughs(map);
    });
  }, [roomId]);

  const ranked = useMemo(() => {
    const totals: Record<string, number> = {};
    room.participants.forEach((p) => { totals[p] = 0; });
    Object.values(laughs).forEach((l) => {
      if (l.targetId in totals) totals[l.targetId] += 1;
    });
    return room.participants
      .map((p) => ({ p, n: totals[p] ?? 0 }))
      .sort((a, b) => b.n - a.n);
  }, [laughs, room.participants]);

  return (
    <div className="game-wrap" style={{ overflowY: "auto" }}>
      <div className="round-strip">
        The verdict 😂 · chat opens in <Countdown endsAt={endsAt} />
      </div>
      <div className="question-card">
        <div className="q-cat">Funniest in the room</div>
        {ranked.map(({ p, n }, i) => {
          const name = p === me
            ? "You"
            : (revealed.has(p) && room.names[p]) ? room.names[p] : "🙈 Hidden";
          return (
            <div
              key={p}
              className="q-opt"
              style={{
                display: "flex", alignItems: "center", gap: 10, cursor: "default",
                ...(i === 0 ? { borderColor: "var(--accent)" } : {}),
              }}
            >
              <b style={{ width: 28, textAlign: "center" }}>{i === 0 ? "👑" : `${i + 1}`}</b>
              <span style={{ flex: 1, minWidth: 0, fontWeight: i === 0 ? 700 : 400 }}>{name}</span>
              <span>😂 <b>{n}</b></span>
            </div>
          );
        })}
        <button className="btn" onClick={onSkip}>Jump into the chat</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decide: the speed-dating finale — match/pass on everyone you unlocked.
// ---------------------------------------------------------------------------
function DecidePhase({
  room, me, revealed, endsAt, onMember, setError,
}: {
  room: RoomDoc; me: string; revealed: Set<string>; endsAt: number;
  onMember: (uid: string, name: string) => void;
  setError: (s: string | null) => void;
}) {
  // Everyone of your preference you met this relay (same-side people were
  // never revealed and aren't up for a decision).
  const candidates = room.participants.filter((p) => p !== me && revealed.has(p));
  const [decided, setDecided] = useState<Record<string, "liked" | "passed">>({});
  const [matched, setMatched] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  async function decide(target: string, liked: boolean) {
    if (busy || decided[target]) return;
    setBusy(target);
    setError(null);
    try {
      const res = await swipe({ targetUid: target, liked });
      setDecided((d) => ({ ...d, [target]: liked ? "liked" : "passed" }));
      if (liked && res.matched) setMatched((m) => [...m, target]);
    } catch (e) {
      setError(friendly(e));
    } finally {
      setBusy(null);
    }
  }

  const allDone = candidates.every((c) => decided[c]);
  return (
    <div className="game-wrap" style={{ overflowY: "auto" }}>
      <div className="round-strip">
        Moment of truth · <Countdown endsAt={endsAt} />
      </div>
      <div className="question-card">
        <div className="q-cat">Who do you want to keep talking to?</div>
        <p className="muted" style={{ fontSize: 13 }}>
          They never see your answer unless it's mutual. Tap a name to peek at
          the profile first.
        </p>
        {candidates.map((p) => {
          const name = room.names[p] ?? "Someone";
          const d = decided[p];
          const isMatch = matched.includes(p);
          return (
            <div key={p} className="q-opt" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                className="bubble-name"
                style={{ flex: 1, minWidth: 0, textAlign: "left" }}
                onClick={() => void onMember(p, name)}
              >
                {name}
              </button>
              {isMatch ? (
                <b style={{ color: "var(--accent)" }}>It's a match ⚡</b>
              ) : d ? (
                <span className="muted">{d === "liked" ? "Liked ♥" : "Passed"}</span>
              ) : (
                <>
                  <button className="decide decide-pass" style={{ width: 40, height: 40, fontSize: 16 }} aria-label={`Pass on ${name}`} disabled={busy === p} onClick={() => void decide(p, false)}>✕</button>
                  <button className="decide decide-like" style={{ width: 40, height: 40, fontSize: 16 }} aria-label={`Match with ${name}`} disabled={busy === p} onClick={() => void decide(p, true)}>♥</button>
                </>
              )}
            </div>
          );
        })}
        {candidates.length === 0 && <p className="muted">Nobody to decide on this time.</p>}
        {allDone && candidates.length > 0 && (
          <p className="muted" style={{ marginTop: 8 }}>
            All set — mutual likes land in Matches. ⚡
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Final open room chat
// ---------------------------------------------------------------------------
function RoomChat({
  room, roomId, me, revealed, setError, onMember,
}: {
  room: RoomDoc; roomId: string; me: string; revealed: Set<string>;
  setError: (s: string | null) => void;
  onMember: (uid: string, name: string) => void;
}) {
  const [msgs, setMsgs] = useState<RoomMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState<Record<string, TeamAnswerDoc>>({});
  const endRef = useRef<HTMLDivElement>(null);

  // Profile photos for the people you're allowed to SEE (mutually compatible —
  // the same set whose names your private view doc carries). Same-side members
  // stay behind the monkey; their photos are never even fetched.
  const [photos, setPhotos] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let alive = true;
    void (async () => {
      const visible = room.participants.filter((p) => p === me || (revealed.has(p) && !!room.names[p]));
      const entries = await Promise.all(visible.map(async (p) => {
        try {
          const s = await getDoc(doc(db, "users", p));
          return [p, ((s.data()?.photoURLs as string[] | undefined)?.[0]) ?? null] as const;
        } catch {
          return [p, null] as const;
        }
      }));
      if (alive) setPhotos(Object.fromEntries(entries));
    })();
    return () => { alive = false; };
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = query(collection(db, "fishbowls", roomId, "messages"), orderBy("sentAt", "asc"));
    return onSnapshot(q, (snap) => {
      setMsgs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<RoomMessage, "id">) })));
    });
  }, [roomId]);

  // Trivia leaderboard for the header strip.
  useEffect(() => {
    if (room.gameType !== "trivia") return;
    return onSnapshot(collection(db, "fishbowls", roomId, "answers"), (snap) => {
      const map: Record<string, TeamAnswerDoc> = {};
      snap.docs.forEach((d) => { map[d.id] = d.data() as TeamAnswerDoc; });
      setAnswers(map);
    });
  }, [roomId, room.gameType]);

  const board = useMemo(() => {
    if (room.gameType !== "trivia" || !room.questions) return null;
    const byQ: Record<number, Record<string, string>> = {};
    Object.values(answers).forEach((a) => {
      (byQ[a.questionIndex] ??= {})[a.pairId] = a.answer;
    });
    const schedule = rotationForRoom(room.participants, room.genders);
    return leaderboard(room.participants, schedule, room.questions, byQ);
  }, [answers, room]);

  useEffect(() => {
    // Scroll only this pane (scrollIntoView also scrolls the document on iOS).
    const el = endRef.current?.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs.length]);

  // When the keyboard opens the pane shrinks — keep it pinned to the latest
  // message instead of leaving the newest ones hidden below the fold.
  useEffect(() => {
    const el = endRef.current?.parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => { el.scrollTop = el.scrollHeight; });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const used = room.used[me] ?? 0;
  const left = Math.max(0, FISHBOWL_MSG_LIMIT - used);

  async function send() {
    const t = draft.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    // Clear now so typing can continue while the send is in flight; restore
    // the draft if it fails.
    setDraft("");
    try {
      await sendFishbowlMessage({ fishbowlId: roomId, text: t });
    } catch (e) {
      setDraft(t);
      setError(friendly(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/* Who's in the room: swipeable row of faces, you first, with each
          person's game score right on their name — this IS the scoreboard.
          Your side of the aisle stays anonymous (🙈): you only ever see
          across your preference. */}
      <div className="avatar-strip">
        {[me, ...room.participants.filter((p) => p !== me)].map((p) => {
          const isMe = p === me;
          const name = isMe ? "You" : room.names[p];
          const vis = isMe || (revealed.has(p) && !!room.names[p]);
          const photo = photos[p];
          const score = board ? ` · ${board[p] ?? 0}` : "";
          return (
            <button
              key={p}
              className="avatar-item"
              disabled={isMe || !vis}
              onClick={() => { if (!isMe && vis) void onMember(p, name!); }}
              aria-label={isMe ? "You" : vis ? `View ${name}'s profile` : "Hidden member"}
            >
              {vis && photo
                ? <img className="avatar-photo" src={photo} alt="" draggable={false} />
                : <div className="avatar-photo avatar-hidden">{vis ? "☺" : "🙈"}</div>}
              <span className="avatar-name">{vis ? name : "Hidden"}{score}</span>
            </button>
          );
        })}
      </div>
      <div className="chat-scroll">
        {msgs.length === 0 && (
          <div className="stub">Room chat is open — {FISHBOWL_MSG_LIMIT} messages each. Shoot your shot.</div>
        )}
        {msgs.map((m) => {
          // Names resolve from YOUR private view doc — a same-side sender has
          // no name on this client at all, so they render emoji'd out. Their
          // message still shows (everyone sees every message).
          const name = room.names[m.senderId];
          const isRevealed = revealed.has(m.senderId) && !!name;
          const photo = photos[m.senderId];
          return (
            <div key={m.id} className={"bubble-row" + (m.senderId === me ? " mine" : "")}>
              {/* Sender avatar, same rule as everywhere: real photo circle for
                  people you can see, monkey for your own side. */}
              {m.senderId !== me && (
                isRevealed && photo ? (
                  <img
                    className="bubble-avatar"
                    src={photo}
                    alt=""
                    draggable={false}
                    onClick={() => void onMember(m.senderId, name)}
                  />
                ) : (
                  <div className="bubble-avatar avatar-hidden">{isRevealed ? "☺" : "🙈"}</div>
                )
              )}
              <div className="bubble">
                {m.senderId !== me && (
                  isRevealed ? (
                    <button className="bubble-name" onClick={() => void onMember(m.senderId, name)}>
                      {name}
                    </button>
                  ) : (
                    <span className="bubble-name" style={{ color: "var(--text-dim)", cursor: "default" }}>Hidden</span>
                  )
                )}
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="chat-compose">
        <div className="compose-pill">
          <textarea
            rows={1}
            value={draft}
            maxLength={2000}
            enterKeyHint="send"
            placeholder={left > 0 ? `Message the room… (${left} left)` : "You're out of messages"}
            /* never `busy` — disabling a focused textarea dismisses the iOS keyboard */
            disabled={left === 0}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
          />
          <button className="send-btn" aria-label="Send" disabled={!draft.trim() || left === 0 || busy} onMouseDown={(e) => e.preventDefault()} onClick={() => void send()}>➤</button>
        </div>
      </div>
    </>
  );
}
